import { useCallback, useMemo } from "react";
import { useAuthState } from "../../../auth/AuthStateProvider";
import { config } from "../../../shared/config/environment";
import { fetchWithRefresh } from "../../../shared/api/fetchWithRefresh";
import { createHttpClient, type HttpClient } from "../services/deckorateApi";

/* ─────────────────────────────────────────────────────────────────────────
 * PARENT hook — the single auth point every feature hook builds on.
 *
 *  builds an HttpClient authorised with the AIVA Azure AD session
 * (bearer = `apiToken ?? graphToken` + APIM key, 401 retry) on the
 * Deckorate content backend (config.deckorateApiBaseUrl — a deployment
 * separate from the general ai-api). API-backed feature hooks call
 * `useDeckorateHttp()` and bind their own service onto the returned client.
 * ──────────────────────────────────────────────────────────────────────── */
export function useDeckorateHttp(): HttpClient {
  const { apiToken, graphToken, refreshTokens } = useAuthState();
  const token = apiToken ?? graphToken;

  const authorizedFetch = useCallback(
    (url: string, init: RequestInit = {}) => {
      const headers: Record<string, string> = {
        ...(init.headers as Record<string, string> | undefined),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };
      return fetchWithRefresh(url, { ...init, headers }, () => refreshTokens());
    },
    [token, refreshTokens],
  );

  const apiBaseUrl = config.deckorateApiBaseUrl;
  return useMemo(
    () => createHttpClient(authorizedFetch, apiBaseUrl),
    [authorizedFetch, apiBaseUrl],
  );
}
